// MyAgent Web - Application Entry Point
// Uses React ChatInterface from myagent_ui

import React, { useEffect, useCallback, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { LocalStorageAdapter } from 'myagent_ui/auth'
import { useAuth } from 'myagent_ui/hooks'
import { AppLayout } from 'myagent_ui/components'
import { createAppContext } from 'myagent_ui/context'
import { commonWorkers } from 'workers-common'
import 'myagent_ui/styles/variables.css'

// Version info
const BUILD_VERSION = __BUILD_VERSION__
console.log(`%c🤖 MyAgent Web v${BUILD_VERSION}`, 'font-weight: bold; font-size: 14px; color: #4CAF50')

// Initialize app context (singleton pattern)
const { auth, getClient, api, llmConfig, sessionConfig, botConfig, peerService, roleCall } = createAppContext({
  storageAdapter: new LocalStorageAdapter(),
})

// Login Modal Component
const LoginModal = () => {
  const handleGoogleLogin = () => {
    const clientId = '999837709442-s8sp31dd0biogn876sne1js9rng788cg.apps.googleusercontent.com'
    // Remove trailing slash for consistent redirect_uri
    const redirectUri = window.location.origin + (window.location.pathname === '/' ? '' : window.location.pathname)
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')

    authUrl.searchParams.set('client_id', clientId)
    authUrl.searchParams.set('redirect_uri', redirectUri)
    authUrl.searchParams.set('response_type', 'code')
    authUrl.searchParams.set('scope', 'openid email profile')
    authUrl.searchParams.set('access_type', 'offline')
    authUrl.searchParams.set('prompt', 'consent')

    window.location.href = authUrl.toString()
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.8)', zIndex: 1000
    }}>
      <div style={{
        background: 'var(--bg-secondary)', padding: 'var(--spacing-xl)',
        borderRadius: 'var(--radius-xl)', maxWidth: '400px', width: '90%',
        textAlign: 'center'
      }}>
        <h2 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--spacing-lg)', color: 'var(--text-primary)' }}>
          Welcome to MyAgent
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-xl)' }}>
          Please sign in to continue
        </p>
        <button onClick={handleGoogleLogin} style={{
          width: '100%', padding: 'var(--spacing-md)', background: 'white',
          border: 'none', borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-base)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 'var(--spacing-sm)'
        }}>
          <span>🔐</span> Continue with Google
        </button>
      </div>
    </div>
  )
}

// Main App Component
const App = () => {
  const isProcessingOAuthRef = useRef(false)

  // Create getUserInfo function that calls user_auth_worker
  const getUserInfoFn = useCallback(async (accessToken) => {
    try {
      const client = getClient()
      return await client.callByNameWithAuth('user-auth-worker', 'getUserInfo', [{ accessToken }])
    } catch (error) {
      console.error('[MyAgent] Failed to get user info:', error)
      return null
    }
  }, [])

  const { isLoggedIn, isLoading, login, logout, userInfo, refresh } = useAuth(auth, getUserInfoFn)

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')

      if (code) {
        if (isProcessingOAuthRef.current) return
        isProcessingOAuthRef.current = true
        try {
          const client = getClient()
          const redirectUri = window.location.origin + (window.location.pathname === '/' ? '' : window.location.pathname)

          const response = await Promise.race([
            client.call(commonWorkers.user_auth_worker, 'auth', [{
              provider: 'google',
              credentials: { code, redirect_uri: redirectUri }
            }]),
            new Promise((_, reject) => setTimeout(() => reject(new Error('RPC call timeout after 30s')), 30000))
          ])

          if (response.accessToken) {
            await login(response.accessToken, response.refreshToken)
          } else {
            console.error('[MyAgent] No accessToken in response:', response)
          }

          window.history.replaceState({}, document.title, window.location.pathname)
        } catch (error) {
          console.error('[MyAgent] OAuth callback error:', error)
          alert(`Login failed: ${error.message}`)
        } finally {
          isProcessingOAuthRef.current = false
        }
      }
    }

    handleOAuthCallback()
  }, [login])

  // Expose client for debugging (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      window.myagentClient = getClient()
    }
  }, [])

  // Refresh user info on login
  useEffect(() => {
    if (isLoggedIn && refresh) refresh()
  }, [isLoggedIn, refresh])

  const handleAuthError = useCallback(async (error) => {
    console.error('[MyAgent] Auth error:', error)
    // Clear tokens and redirect to login
    await logout()
    window.location.reload()
  }, [logout])

  const handleLogout = useCallback(async () => {
    console.log('[MyAgent] Logging out...')
    await logout()
    window.location.reload()
  }, [logout])

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }}>
        Loading...
      </div>
    )
  }

  // Show login modal if not logged in
  if (!isLoggedIn) {
    return <LoginModal />
  }

  return (
    <div className="app" data-theme="dark" style={{ height: '100%' }}>
      <AppLayout
        api={api}
        onAuthError={handleAuthError}
        onLogout={handleLogout}
        emptyMessage="How can I help you today?"
        placeholder="Message MyAgent..."
        userInfo={userInfo}
        llmConfig={llmConfig}
        sessionConfig={sessionConfig}
        peerService={peerService}
        roleCall={roleCall}
      />
    </div>
  )
}

/**
 * Initialize the application
 */
const init = () => {
  const root = createRoot(document.getElementById('app'))
  root.render(<App />)
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
